import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Formik } from 'formik';
import employeeAttendanceAPI from '../api/employeeAttendanceAPI';
import { useEmployeeAttendanceAuth } from '../context/EmployeeAttendanceAuthContext';
import { MdLock, MdArrowBack } from 'react-icons/md';
import '../employeeAttendance.css';
import '../employeeAttendance-compact.css';

const validationSchema = yup.object({
  current_password: yup.string().required('Current password is required'),
  new_password: yup
    .string()
    .min(8, 'Minimum 8 characters')
    .required('New password is required'),
  confirm_password: yup
    .string()
    .oneOf([yup.ref('new_password'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

function ChangePassword() {
  const { getToken, logout } = useEmployeeAttendanceAuth();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (values, actions) => {
    const token = getToken();
    if (!token) {
      setError('Session expired. Please login again.');
      return;
    }

    try {
      actions.setSubmitting(true);
      setError(null);
      setSuccess(null);

      const result = await employeeAttendanceAPI.changePassword(
        values.current_password,
        values.new_password,
        token
      );

      if (result?.success) {
        const revoked = result.tokens_revoked || 0;
        setSuccess(
          result.message ||
            `Password changed successfully. ${revoked > 0 ? `${revoked} session(s) logged out.` : ''} Please login again with your new password.`
        );

        setTimeout(() => {
          logout({ navigateAway: true });
        }, 3000);
      } else {
        setError(result?.message || 'Password change failed');
      }
    } catch (err) {
      setError(err.message || 'Password change failed');
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <div className="ea-main">
      <div className="ea-topbar">
        <div>
          <div className="ea-greeting">Change Password</div>
          <div className="ea-dateline">Update your account password</div>
        </div>
      </div>

      <div className="ea-change-password-card">
        {success ? (
          <div className="ea-success-block">
            <div className="ea-success-icon">
              <MdLock size={32} />
            </div>
            <p className="ea-success-text">{success}</p>
            <p className="ea-success-sub">Redirecting to login…</p>
          </div>
        ) : (
          <>
            {error && (
              <div
                className="ea-error-banner"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginBottom: '20px',
                  padding: '14px 16px',
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  lineHeight: '1.4',
                  background: '#fef2f2',
                  color: '#991b1b',
                  border: '1px solid rgba(239,68,68,0.2)',
                }}
              >
                <span style={{ fontSize: '1.2rem', flexShrink: 0, marginTop: '1px' }}>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <Formik
              initialValues={{ current_password: '', new_password: '', confirm_password: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <label className="block mb-4" htmlFor="current_password">
                    <span className="ea-field-label">Current Password</span>
                    <input
                      id="current_password"
                      name="current_password"
                      type="password"
                      placeholder="Enter current password"
                      value={values.current_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="ea-field-input"
                    />
                    {touched.current_password && errors.current_password && (
                      <p className="text-red-600 text-xs mt-1">{errors.current_password}</p>
                    )}
                  </label>

                  <label className="block mb-4" htmlFor="new_password">
                    <span className="ea-field-label">New Password</span>
                    <input
                      id="new_password"
                      name="new_password"
                      type="password"
                      placeholder="Min 8 characters"
                      value={values.new_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="ea-field-input"
                    />
                    {touched.new_password && errors.new_password && (
                      <p className="text-red-600 text-xs mt-1">{errors.new_password}</p>
                    )}
                  </label>

                  <label className="block mb-5" htmlFor="confirm_password">
                    <span className="ea-field-label">Confirm New Password</span>
                    <input
                      id="confirm_password"
                      name="confirm_password"
                      type="password"
                      placeholder="Re-enter new password"
                      value={values.confirm_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="ea-field-input"
                    />
                    {touched.confirm_password && errors.confirm_password && (
                      <p className="text-red-600 text-xs mt-1">{errors.confirm_password}</p>
                    )}
                  </label>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="ea-btn-back"
                      onClick={() => navigate('/bu-dashboard/profile')}
                    >
                      <MdArrowBack size={16} />
                      Back
                    </button>
                    <button type="submit" disabled={isSubmitting} className="ea-btn-submit flex-1">
                      {isSubmitting ? 'Changing…' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </>
        )}
      </div>
    </div>
  );
}

export default ChangePassword;